import {useLocalStorage} from '../common/localStorage'
import {API} from '../common/axios'
import {jwtDecode} from 'jwt-decode'
import {useNavigate} from 'react-router-dom'
import {useEffect, useState} from 'react'
import {RouteSecurity} from '../common/RouteSecurity'
import {webEvents} from '../common/WebEvents'
import {SearchUsersResult, UserSearchParams} from '../inventory/utils'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../store'
import { setUser, clearUser } from '../store/slices/user'
import {clearError} from '../store/slices/error'

/* local storage user key */
export const LOCAL_STORAGE_REMEMBER_ME = 'techgo:remember_me'

type JwtUserClaim = {
    user_id: string
    type: string
    permissions: string
    truck: string | null
    full_name: string | null
    mobile: string | null
    clocked_in: string | null
}

export type AuthChallenge = {
    challengeId: string
    expiresAt: Date
}

export function useAuth() {
    const user = useSelector((state: RootState) => state.user);
    const [rememberMe, setRememberMe] = useLocalStorage<string | null>(LOCAL_STORAGE_REMEMBER_ME, null)
    const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false)
    const [token, setToken] = useState<string | null>(null)
    const navigate = useNavigate()
    const dispatch = useDispatch()

    useEffect(() => {
        const userToken = user?.token
        const isTokenChanged = userToken !== token

        if (isTokenChanged) {
            const isUserLoggedIn = !!userToken
            setIsLoggedIn(isUserLoggedIn)
            setToken(userToken || null)
        }
    }, [user?.token, token])

    async function login(
        username: string,
        password: string,
        authChallengeId?: string
    ): Promise<AuthChallenge | string> {
        setUser(null)

        let resp = await API.post('iam/authenticate', {
            username,
            password,
            auth_challenge_id: authChallengeId,
            remember_me: authChallengeId ? undefined : (rememberMe ?? undefined)
        })

        if (resp.data.access_token) {
            setAuthInfo(resp.data.access_token, resp.data.clocked_in)
            setIsLoggedIn(true)
            return resp.data.access_token
        }

        setRememberMe(null)
        return {
            challengeId: resp.data.auth_challenge_id,
            expiresAt: new Date(resp.data.expires_at)
        }
    }

    /** Verify the one-time-password for the given challenge
     * @param {string} challengeId
     * @param {string} otp one-time password
     * @return {string} The h3 token
     */
    async function verify(challengeId: string, otp: string): Promise<string> {
        let resp = await API.post('iam/token', {
            auth_challenge_id: challengeId,
            otp
        })

        setAuthInfo(resp.data.access_token, resp.data.clocked_in)
        if (resp.data.remember_me)
            setRememberMe(resp.data.remember_me.token)

        return resp.data.access_token
    }

    function setAuthInfo(token: string, clocked_in: string | null) {
        let user = (jwtDecode(token) as any).user as JwtUserClaim

        dispatch(setUser({
            token,
            user_id: user.user_id,
            type: user.type,
            permissions: RouteSecurity.fromMask(user.permissions).permissions,
            truck: user.truck,
            full_name: user.full_name,
            mobile: user.mobile,
            clocked_in
        }))
    }

    function logout(message: string = '', isError: boolean = false): void {
        navigate('/login', {state: {message, isError}})
        dispatch(clearUser())
    }

    function getUser() {
        return user
    }

    function isTokenExpired(): boolean {
        let expired = true

        if (user?.token) {
            let expTime = (jwtDecode(user.token).exp ?? 0) * 1000
            if ((expired = expTime < Date.now()))
                webEvents.debug(`H3 Token expired at ${new Date(expTime).toLocaleString()}`)
        }

        return expired
    }

    async function clockIn(): Promise<void> {
        if (user && user.token) {
            try {
                const resp = await API.post('iam/clock-in', {})
                dispatch(setUser({...user, clocked_in: resp.data.clocked_in}))
                dispatch(clearError());
            } catch (error) {
                console.error('Error in clocking in:', error)
                throw error
            }
        } else {
            navigate('/login')
        }
    }

    async function clockOut(): Promise<void> {
        if (user && user.token) {
            try {
                const resp = await API.post('iam/clock-out', {})

                if (resp.status === 204) {
                    dispatch(setUser({...user, clocked_in: resp.data.clocked_in}))
                } else {
                    throw new Error('Failed to clock out.')
                }
            } catch (error) {
                console.error('Error in clocking out:', error)
                throw error
            }
        } else {
            throw new Error('Failed to clock out.')
        }
    }

    async function searchUsers(params: any): Promise<SearchUsersResult[]> {
        const response = await API.get(`iam/users`, {params})
        return response.data
    }

    return {
        getUser: () => getUser(),
        getTruck: () : string => user?.truck ?? '',
        isLoggedIn, // check if the user token is truthy
        getPermissions: () => getUser()?.permissions ?? [],
        token,
        getName: () => user?.full_name ?? '',
        getEmail: () => user?.user_id ?? '',
        searchUsers: (permissions: UserSearchParams | {}) => searchUsers(permissions),
        isTokenExpired,
        verify,
        login,
        logout,
        clockIn,
        clockOut
    }
}
